import { Box, Button, Grid2 as Grid } from "@mui/material"
import Stack from "@mui/material/Stack"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useTranslation } from "react-i18next"
import DialogForm from "../components/DialogForm"
import Layout from "../components/Layout"
import CopiaForm from "../components/forms/CopiaForm"
import { useQsFilters } from "../hooks/filters"
import { useCopiaCreate, useCopieList, useCopiaDelete, useCopiaUpdate } from "../hooks/copie"
import { Copia } from "../types"
import CopiaCard from "../components/CopiaCard"
import DialogConfirm from "../components/DialogConfirm"
import { useSnackbarMessage } from "../hooks/snackbar"
import useUserCanWriteAndDelete from "../hooks/permissions"
import { useIsMobile } from "../hooks/mobile"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "titolo",
  formato: params.get("formato") ?? "",
  elemento: params.get("elemento") ?? "",
})

type CopieProps = {
  fileDigitaleId: number
}

export default function Copie({ fileDigitaleId }: CopieProps) {
  const { t } = useTranslation()
  const { filters } = useQsFilters(initFilters)
  const userCanWriteAndDelete = useUserCanWriteAndDelete()
  const isMobile = useIsMobile()

  const { data } = useCopieList({
    page: String(filters.page),
    search: filters.search,
    ordering: filters.ordering,
    file_digitale: fileDigitaleId.toString(),
  })

  const { mutateAsync: create } = useCopiaCreate()
  const { mutateAsync: update } = useCopiaUpdate()
  const { mutateAsync: remove } = useCopiaDelete()

  const [createModalState, createModalActions] = useModalTrigger()
  const [updateModalState, updateModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()

  const showMessage = useSnackbarMessage()

  return (
    <Layout>
      <Stack sx={{ minHeight: "100%" }}>
        <Box
          sx={{
            textAlign: "right",
            position: "sticky",
            top: 0,
            zIndex: 1,
            justifyContent: "flex-end",
            backgroundColor: "grey.100",
            borderBottom: 1,
            borderColor: "grey.300",
          }}
          px={3}
          py={1}
        >
          {userCanWriteAndDelete && !isMobile && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => {
                createModalActions.open()
              }}
            >
              {t("copia.cta_aggiungi")}
            </Button>
          )}
        </Box>

        <Box flexGrow={1} px={3} pb={3} pt={1}>
          <Grid container spacing={2}>
            {data?.results.map((copia) => (
              <Grid
                size={{
                  xs: 12,
                  sm: 12,
                  md: 6,
                  lg: 6,
                  xl: 4,
                }}
                key={copia.id}
              >
                <CopiaCard
                  copia={copia}
                  onUpdate={() => {
                    updateModalActions.open(copia)
                  }}
                  onDelete={() => {
                    deleteModalActions.open(copia)
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Stack>

      <DialogForm<Copia>
        open={createModalState.isOpen}
        hasCancel
        handleClose={createModalActions.close}
        title={t("copia.cta_aggiungi")}
        FormComponent={CopiaForm}
        save={(values: Copia) => {
          const newValues = {
            ...values,
            file_digitale: fileDigitaleId,
          }
          return create(newValues as Copia).then(() => {
            createModalActions.close()
          })
        }}
      />
      <DialogForm<Copia>
        open={updateModalState.isOpen}
        hasCancel
        handleClose={updateModalActions.close}
        title={t("copia.cta_modifica")}
        FormComponent={CopiaForm}
        item={updateModalState.value}
        save={(values: Copia) => {
          return update(values).then(() => {
            updateModalActions.close()
            showMessage(t("copia.esito_modifica_ok"))
          })
        }}
      />

      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("copia.cta_elimina")}
        subtitle={
          <span>
            {t("copia.cta_subtitle_elimina")} <b>"{deleteModalState.value?.data}"</b>&nbsp;?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id).then(() => {
            deleteModalActions.close()
            showMessage(t("copia.esito_elimina_ok"))
          }).catch(() => {
            deleteModalActions.close()
            showMessage(t("general.esito_elimina_errore"), "error")
          })
        }}
      />
    </Layout>
  )
}
